import { useState, type ReactNode } from 'react';
import { useNewVersionCheck, type NewVersionCheckOptions } from '../lib/new-version';
import { useUploadsInProgress } from '../lib/upload-activity';
import { Button } from './button';
import { Icon } from './icons';

export interface NewVersionBannerProps extends NewVersionCheckOptions {
  /** What Reload does; defaults to reloading the page. */
  onReload?: () => void;
  /** A "?" help tip shown after the message. */
  messageAside?: ReactNode;
}

/**
 * "A new version of ClosingVault is available. [Reload]" — shown once a newer
 * build is deployed than the one this tab runs ({@link useNewVersionCheck}).
 * Never reloads by itself: the user may be mid-form or mid-upload, and while an
 * upload is running it says so (the button stays). Dismissible for the rest of
 * the tab's life.
 *
 * Mount it AFTER the page content: it is a sticky bottom bar in the normal
 * flow, so it rides the bottom of the viewport but at the end of the page takes
 * its own space instead of covering the last of the content. The polite live
 * region is always mounted so the message is announced when it appears.
 */
export function NewVersionBanner({ onReload, messageAside, ...options }: NewVersionBannerProps) {
  const { latestVersion } = useNewVersionCheck(options);
  const uploading = useUploadsInProgress();
  const [dismissed, setDismissed] = useState(false);
  const show = latestVersion !== undefined && !dismissed;

  return (
    <div role="status" aria-live="polite" className="sticky bottom-0 z-40">
      {show ? (
        <div className="border-t bg-surface shadow-md">
          <div className="mx-auto flex w-full max-w-container items-center gap-3 px-4 py-2 md:px-6">
            <div className="flex min-w-0 flex-1 items-center gap-1.5">
              <p className="min-w-0 text-sm text-ink">
                A new version of ClosingVault is available.
                {uploading ? ' Reload when your uploads finish.' : null}
              </p>
              {messageAside}
            </div>
            <Button
              type="button"
              size="sm"
              variant={uploading ? 'outline' : 'default'}
              onClick={onReload ?? (() => window.location.reload())}
            >
              Reload
            </Button>
            <button
              type="button"
              aria-label="Dismiss"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-ink-3 hover:bg-surface-2 hover:text-ink max-md:min-h-tap max-md:min-w-tap"
              onClick={() => setDismissed(true)}
            >
              <Icon name="close" />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
